/**
 * useTrailingRefetch — coalesce a burst of realtime signals into refetches that never lose the last one.
 * P13-V1-V6B-JOINT-INCIDENT-PARENT-CLOSURE-REPAIR-R1 (REPAIR-04).
 *
 * The previous coalescer was `if (inFlight) return`. That drops every signal that arrives while a
 * refetch is running — including the LAST one of a burst. Create → assign → status in quick
 * succession would leave the view showing the state as of the first signal, silently stale, with no
 * further event coming to correct it.
 *
 * This is the trailing-edge variant: the first signal starts a refetch, any signal arriving during
 * that refetch marks the result dirty, and when it settles the loop runs once more. So the view
 * always ends reconciled with a server read taken AFTER the last signal, while N signals still cost
 * at most 2 in-flight requests rather than N.
 *
 * What it deliberately is not: no queue of one request per frame, no polling, no timer, no store.
 * And it changes nothing about authority — every refetch is the same canonical, server-scoped read;
 * a payload is never the source of what gets displayed.
 */
export type TrailingRefetch = (() => Promise<void>) & { readonly isRunning: () => boolean }

export function useTrailingRefetch(refetch: () => Promise<unknown>): TrailingRefetch {
  let inFlight = false
  let dirty = false

  const trigger = async (): Promise<void> => {
    // A refetch is already running: remember that its result will be stale and let it finish.
    if (inFlight) {
      dirty = true
      return
    }
    inFlight = true
    try {
      do {
        dirty = false
        await refetch()
      } while (dirty) // a signal landed mid-flight — reconcile again against the newer state
    } finally {
      inFlight = false
    }
  }

  return Object.assign(trigger, { isRunning: () => inFlight }) as TrailingRefetch
}

export default useTrailingRefetch
